import React, {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { Table } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';
import { CopyableCell } from 'src/components/CopyableCell';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { useFilterQueryEffect } from 'src/hooks/useFilterQueryEffect';
import {
  fetchReviewQueue,
  formatCaseUserName,
  formatOpenFor,
  type ReviewQueueItem,
  type ReviewQueueKind,
} from 'src/api/adminCompliance';
import { formatUserStatusLabel } from 'src/api/adminUsers';
import { ROUTE_USERS } from 'src/utils/routes';

import { StatusBadge, UserLink } from './styled';

// Funds are only held at HIGH and above, so the lower levels would never match
// a row in this queue.
const RISK_OPTIONS = [
  { value: 'HIGH', label: 'High' },
  { value: 'PROHIBITED', label: 'Prohibited' },
];

// One bar per filter. Address and Transaction ID both travel through the
// endpoint's single `field`/`value` pair, so they are mutually exclusive:
// typing in one clears the other (see handleFilterChange).
const QUEUE_FILTERS: FilterDef[] = [
  { key: 'risk_level', label: 'Risk', options: RISK_OPTIONS, allLabel: 'All risk levels' },
  { key: 'address', label: 'Address', placeholder: 'Starts with…' },
  { key: 'id', label: 'Transaction ID', placeholder: 'Exact id' },
];

const EXCLUSIVE_SEARCH: Record<string, string> = { address: 'id', id: 'address' };

const HEADERS = [
  'User',
  'Account status',
  'Amount',
  'Address',
  'Risk',
  'Case #',
  'Held for',
];

type ReviewQueueTabProps = {
  kind: ReviewQueueKind;
  onTotalChange?: Dispatch<SetStateAction<number | null>>;
};

const ReviewQueueTab: React.FC<ReviewQueueTabProps> = ({ kind, onTotalChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const load = useCallback(
    async (params: { page: number; filters: Record<string, string> }) => {
      const { risk_level, address, id } = params.filters;
      // Only one of address / id is ever set; whichever it is becomes the pair.
      const field = id ? 'id' : address ? 'address' : undefined;
      setLoading(true);
      try {
        const res = await fetchReviewQueue(kind, {
          page: params.page,
          limit: 10,
          order: 'desc',
          risk_level: risk_level || undefined,
          field,
          value: field ? params.filters[field] : undefined,
        });
        setItems(res.items);
        setTotalPages(Math.max(1, res.meta?.page_count || 1));
        onTotalChange?.(res.meta?.total_count || 0);
      } catch (e: any) {
        dispatch(
          setAppAlert({
            message: e?.response?.data?.error ?? 'Failed to load the review queue',
            isSuccess: false,
          }),
        );
        setItems([]);
        onTotalChange?.(0);
      } finally {
        setLoading(false);
      }
    },
    // `kind` is part of the query: switching queues re-runs it from page 1.
    [dispatch, kind, onTotalChange],
  );

  const { refresh: refreshQuery } = useFilterQueryEffect({ filters, currentPage, setCurrentPage, query: load });

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      const other = EXCLUSIVE_SEARCH[key];
      if (other && value) next[other] = '';
      return next;
    });
  };

  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        user_name: formatCaseUserName(item.user),
        account_status: formatUserStatusLabel(item.user?.status ?? undefined),
        amount_display: `${item.amount ?? '—'} ${item.currency_code ?? ''}`.trim(),
        // One column for both queues: a deposit's counterparty is the sender,
        // a withdrawal's is the destination.
        address_display: item.sender_address ?? item.destination_address ?? '—',
        risk_display: item.screening?.risk_level || '—',
        case_display: item.compliance_case?.case_number
          ? `#${item.compliance_case.case_number}`
          : '—',
        held_for_display: formatOpenFor(item.held_for_ms),
      })),
    [items],
  );

  return (
    <>
      <FilterBar
        filters={QUEUE_FILTERS}
        values={filters}
        onChange={handleFilterChange}
        onClear={() => setFilters({})}
        onRefresh={refreshQuery}
      />

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={HEADERS}
          items={rows}
          columns="1.2fr 0.9fr 1fr 1.3fr 0.8fr 0.7fr 0.7fr"
          fieldsToShow={[
            'user_name',
            'account_status',
            'amount_display',
            'address_display',
            'risk_display',
            'case_display',
            'held_for_display',
          ]}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          renderCell={(item, field) => {
            if (field === 'user_name') {
              const value = String(item.user_name || '—');
              const userId = item.user?.id;
              if (!userId) return <span>{value}</span>;
              return (
                <UserLink
                  type="button"
                  title={item.user?.email ? `${value} (${item.user.email})` : value}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${ROUTE_USERS}/${userId}`);
                  }}
                >
                  {value}
                </UserLink>
              );
            }
            if (field === 'address_display') {
              return item.address_display === '—' ? (
                <span>—</span>
              ) : (
                <CopyableCell value={item.address_display} label="Address" />
              );
            }
            if (field === 'risk_display') {
              return item.risk_display === '—' ? (
                <span>—</span>
              ) : (
                <StatusBadge $status={item.risk_display}>{item.risk_display}</StatusBadge>
              );
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}
    </>
  );
};

export default ReviewQueueTab;
